import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./ClaspPaywall.module.scss"

/**
 * ClaspPaywall (`/clasp`) — an original, self-contained NON-ONBOARDING upsell
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * NON-ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — but told in the
 * warmest, most human language security has: a PRIVATE DIARY. Your memories are
 * the diary of your life; premium is the little brass CLASP that closes it, and
 * the clasp opens for one person only — you.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The diary only you can open." The hero is a leather memory-journal, its
 * fore-edge fanned with your real photo pages and a memory framed on the cover.
 * A leather strap crosses the cover to a brass lock. DRAW THE CLASP — pull the
 * buckle across into the keeper and the strap draws taut, the lock turns and
 * thunks shut, the cover's PRIVATE crest lights, the status flips
 * "anyone can open it → only you can open it", and the four real safeguards
 * clasp on one by one. Your photos never move or hide from YOU — the clasp only
 * decides who else can lift the cover: no one.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from its security neighbours (Static/Cipher encryption
 * consoles, Eyes Only redacted dossier, Safelight darkroom, Ward crest+ring,
 * Sanctum concentric layers, OnlyYou keypad, Trace pattern, Imprint fingerprint,
 * Vault Door, Reissue card-reader, Shade window-blind, Bystander dual-reality,
 * PlainSight disguise, Strongbox safe-deposit wall) and from the warm album
 * cluster (Keepsake deck, Patchwork quilt):
 * Layout      : a CLASP-LOCKED LEATHER JOURNAL — a single tactile keepsake
 *               object with a cover photo, fanned photo page-edges, a wrapping
 *               strap and a brass lock. Not a wall/door/window/console/keypad/
 *               crest/concentric-ring/deck/quilt.
 * Interaction : DRAW-THE-CLASP — pull (or tap) the brass buckle across the
 *               fore-edge into its keeper; the strap draws taut and the lock
 *               turns. New verb to the set: buckle-across — not zip (Carryall/
 *               Seam), haul (Beacon), seat-inward (Sanctum), draw-shade (Shade),
 *               feed-card (Reissue), slide-tray (Strongbox), keypad (OnlyYou),
 *               pattern (Trace), dial (Tumbler) or handover-slider (Bystander).
 * Metaphor    : a PRIVATE DIARY / clasp lock — the most human privacy object
 *               there is. Not encryption, not a vault door, not a disguise.
 * Motion      : the strap sweeping taut, the brass hasp rotating shut with a
 *               settle, a warm "clasped" bloom, safeguards latching one by one.
 * Feature     : the real protections are what the clasp guarantees, latched on
 *               as it closes.
 * Value frame : SOLE-CUSTODY + INTIMACY — "your life's diary; it opens for one
 *               person." Commitment + endowment (it's already yours; clasp it
 *               for the year). Not fear.
 * CTA         : COMMITMENT — "Clasp my vault for a year"; yearly featured/default.
 * Register    : intimate, warm, personal-secret — a keepsake, not a fortress;
 *               a deliberate turn away from the cold-tech security cluster.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock
 * with Face ID / Touch ID / code · encrypted Cloud Vault · Break-in reports ·
 * App disguise). The "clasp" is an honest visual metaphor for an access-control
 * lock — the copy states plainly that photos are locked with your Face ID / code
 * and encrypted in your Cloud Vault, open to you and no one else. This is a
 * non-onboarding upsell, so nothing is at stake: the clasp only governs who else
 * can open the diary — every photo stays exactly where it is, no data-loss
 * threat, no countdown, no fabricated scarcity. "Save 79%" is real arithmetic
 * from live product data. Yearly is the featured, default, most prominent price;
 * monthly is small and secondary. The 7-day free trial is the real trial.
 * Renders on built-in debug product data with bundled photos — no native
 * injection, no external egress.
 */

// The real Private Photo Vault safeguards — what the clasp guarantees. Each
// latches on (in order) as the diary closes.
const GUARDS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted copy of every page",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "a silent photo of anyone who tries the lock",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "the diary can pass for an ordinary app",
  },
]

// Photo prints tucked in the diary, peeking past the fore-edge — bundled,
// self-contained memories reused from the repo. Read as "this diary is full of
// your photos." Each has a slight angle so they stack like a real handful.
const PAGES = [
  { src: "/clasp/p3.jpg", r: -7 },
  { src: "/clasp/p1.jpg", r: 4 },
  { src: "/clasp/p5.jpg", r: -3 },
]

const ClaspPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Warm the safe-area insets to the desk so the keepsake sits on one surface.
  useEffect(() => {
    actions.setHeaderInsetColor("#efe6d8")
    actions.setFooterInsetColor("#efe6d8")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the clasp: false = open (anyone can lift the cover), true = clasped ────
  const [clasped, setClasped] = useState(false)
  const userActed = useRef(false)

  const toggleClasp = useCallback(() => {
    userActed.current = true
    setClasped((c) => !c)
  }, [])

  // Auto-demo one clasp on arrival so the payoff reads at a glance.
  useEffect(() => {
    if (reduceMotion) {
      setClasped(true)
      return
    }
    const t = window.setTimeout(() => {
      if (!userActed.current) setClasped(true)
    }, 780)
    return () => window.clearTimeout(t)
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div className={`${styles.root} ${clasped ? styles.isClasped : ""}`}>
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Your private diary
          </span>
          <h1 className={styles.title}>
            The diary only <span className={styles.titleAccent}>you</span> can
            open.
          </h1>
          <p className={styles.sub}>
            Your memories are the diary of your life. Premium is the clasp that
            closes it — and it opens for one person.
          </p>
        </section>

        {/* ── the journal (hero) ─────────────────────────────────────────── */}
        <section className={styles.stage}>
          <button
            className={styles.journal}
            onClick={toggleClasp}
            aria-pressed={clasped}
            aria-label={clasped ? "Unclasp the diary" : "Clasp the diary shut"}
          >
            {/* photo prints tucked in, peeking past the fore-edge (right) */}
            <div className={styles.pages} aria-hidden="true">
              {PAGES.map((p, i) => (
                <span
                  key={i}
                  className={styles.page}
                  style={{ ["--i" as any]: i, ["--r" as any]: `${p.r}deg` }}
                >
                  <span
                    className={styles.pagePhoto}
                    style={{ backgroundImage: `url(${p.src})` }}
                  />
                </span>
              ))}
            </div>

            {/* the leather cover */}
            <div className={styles.cover}>
              <span className={styles.grain} aria-hidden="true" />
              <span className={styles.edgeStitch} aria-hidden="true" />

              {/* a memory framed on the cover */}
              <span className={styles.coverPhoto} aria-hidden="true">
                <span
                  className={styles.coverPhotoImg}
                  style={{ backgroundImage: "url(/clasp/cover.jpg)" }}
                />
              </span>

              <span className={styles.plate}>
                <span className={styles.plateTitle}>Our&nbsp;memories</span>
                <span className={styles.plateSub}>Private Photo Vault</span>
              </span>

              {/* embossed PRIVATE crest — lights when clasped */}
              <span className={styles.crest} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.2 12.1l2 2 3.6-3.9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className={styles.crestWord}>PRIVATE</span>
              </span>

              {/* the clasp: strap + brass lock */}
              <span className={styles.strap} aria-hidden="true">
                <span className={styles.strapStitch} />
              </span>
              <span className={styles.keeper} aria-hidden="true" />
              <span className={styles.lock} aria-hidden="true">
                <span className={styles.lockBody}>
                  <span className={styles.hasp} />
                  <span className={styles.keyhole} />
                </span>
                <span className={styles.lockGleam} />
              </span>
            </div>

            {/* status ribbon under the object */}
            <span
              className={`${styles.status} ${clasped ? styles.statusOn : ""}`}
              aria-live="polite"
            >
              <span className={styles.statusDot} aria-hidden="true" />
              {clasped
                ? "Clasped · only you can open it"
                : "Open · anyone can lift the cover"}
            </span>
          </button>

          {/* the single decisive affordance */}
          <button
            className={`${styles.claspBtn} ${clasped ? styles.claspBtnOn : ""}`}
            onClick={toggleClasp}
            aria-pressed={clasped}
          >
            <span className={styles.claspBtnIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="17" height="17">
                {clasped ? (
                  <>
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9.5"
                      rx="2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </>
                ) : (
                  <>
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9.5"
                      rx="2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M8 10.5V8a4 4 0 0 1 7.7-1.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </>
                )}
              </svg>
            </span>
            <span className={styles.claspBtnText}>
              {clasped ? "Clasped to you — tap to open" : "Draw the clasp"}
            </span>
          </button>
        </section>

        {/* ── what the clasp guarantees (real safeguards) ────────────────── */}
        <ul className={styles.guards}>
          <li className={styles.guardsHead}>
            <span>What the clasp keeps</span>
            <span className={styles.guardsCount}>
              {clasped
                ? `${GUARDS.length}/${GUARDS.length}`
                : `0/${GUARDS.length}`}{" "}
              latched
            </span>
          </li>
          {GUARDS.map((g, i) => (
            <li
              key={g.key}
              className={`${styles.guard} ${clasped ? styles.guardOn : ""}`}
              style={{ ["--d" as any]: i }}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <g.Icon width={16} height={16} />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardChip} aria-hidden="true">
                {clasped ? (
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  "•"
                )}
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          The clasp only decides who else can open it — never you. Every photo
          stays exactly where it is, locked with your Face&nbsp;ID or code and
          encrypted in your Cloud Vault.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                A full year, clasped
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Clasp my vault — start ${trialDays}-day free trial`
              : "Clasp my vault"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClaspPaywall
